import { Core, Decorative } from "@faire/design-tokens";
import { trackCoreCollectionsCollectionTileClick } from "@faire/web-api/events/coreCollections/click/collectionTile";
import { trackCoreCollectionsProductBasedCollectionTileView } from "@faire/web-api/events/coreCollections/view/productBasedCollectionTile";
import { ICollectionTile } from "@faire/web-api/indigofair/data/ICollectionTile";
import { IProductBasedCollectionMetadata } from "@faire/web-api/indigofair/data/IProductBasedCollectionMetadata";
import { localNum } from "@faire/web/common/localization";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Link } from "@faire/web/ui/LinkAnchor";
import React, { useEffect, useState } from "react";

import { CollectionImpression } from "@faire/retailer/components/CollectionDiscovery/config";
import { getProductBasedCollectionLinkTo } from "@faire/retailer/features/MarketplaceItems/collectionUtils";
import {
  IProductImageProps,
  ProductImage,
} from "@faire/retailer/features/MarketplaceItems/ProductImage";
import { ImageContainer } from "@faire/retailer/features/MarketplaceItems/styled";
import { CollectionTileClickImpressionContext } from "@faire/retailer/hooks/CollectionTileImpression/CollectionTileClickImpressionContext";
import { useCollectionTileViewImpression } from "@faire/retailer/hooks/CollectionTileImpression/useCollectionTileViewImpression";
import { bpsToPercent } from "@faire/retailer/lib/bpsToDecimalAndPercent";

interface ICollectionTileProps {
  collection: IProductBasedCollectionMetadata;
  imageDimension?: IProductImageProps["imageDimension"];
  source?: CollectionImpression;
  onClick?: () => void;
}

export const CollectionTile: React.FC<ICollectionTileProps> = ({
  collection,
  imageDimension,
  source,
  onClick,
}) => {
  const linkTo = useCollectionLinkTo(collection);
  const itemsCountNumber = useCollectionItemsCount(collection);

  const { contextToken, requestId, screenContext, containerContext } =
    React.useContext(CollectionTileClickImpressionContext);

  const onClickEvent = () => {
    onClick?.();
    trackCoreCollectionsCollectionTileClick(
      contextToken ?? "",
      collection.product_based_collection_token ?? "",
      requestId ?? "",
      screenContext ?? ICollectionTile.Screen.SCREEN_UNKNOWN,
      containerContext ??
        ICollectionTile.ScreenContainer.SCREEN_CONTAINER_UNKNOWN,
      collection.collection_promo?.brand_promo_collection_token ?? ""
    );
  };

  const trackCollectionTileProps = useCollectionTileViewImpression({
    collectionToken: collection.product_based_collection_token ?? "",
  });

  const visibleRef = useDoActionWhenVisible(() => {
    trackCoreCollectionsProductBasedCollectionTileView(
      collection.product_based_collection_token ?? "",
      source ?? CollectionImpression.COLLECTION_TILE,
      collection.collection_promo?.brand_promo_collection_token ?? ""
    );
  });

  return (
    <Link
      to={linkTo ?? ""}
      onClick={onClickEvent}
      data-test-id="collection-tile"
      {...trackCollectionTileProps}
      ref={(element) => {
        trackCollectionTileProps?.ref?.(element);
      }}
    >
      <Column ref={visibleRef}>
        <ImageContainer>
          <ProductImage
            image={collection.image}
            imageDimension={imageDimension}
            showVerticalImagery={false}
            alt={collection.name ?? ""}
          />
        </ImageContainer>
        <Spacer height="1x" />
        <Typography variant="paragraphSansMedium" truncate>
          {collection.name}
        </Typography>
        <Typography color={Core.text.subdued}>
          <StrictLocalMsg
            defaultMessage="{itemsCountNumber, plural, one {# item} other {# items}}"
            values={{ itemsCountNumber }}
            description={{
              text: "Number of items from a collection",
              img: "https://cdn.faire.com/fastly/45a08ad3e60fa03f5a184ae9223df90aaa1cf2c6fb9aaf04f30b562d12b9fbaa.png",
            }}
          />
        </Typography>
        <CollectionDiscountTag collection={collection} />
      </Column>
    </Link>
  );
};

const CollectionDiscountTag: React.FC<{
  collection: IProductBasedCollectionMetadata;
}> = ({ collection }) => {
  const showFreeDisplayTag =
    collection.active_display_promo?.type === "FREE_DISPLAY";
  const showDiscountPercentageTag = !!collection.collection_promo?.discount_bps;

  if (showFreeDisplayTag && showDiscountPercentageTag) {
    return (
      <Typography color={Decorative.shade.orange}>
        <StrictLocalMsg
          defaultMessage="Free display + {discountPercentageNumber}% off"
          description={{
            text: "This collection offers a free display product and products in this collection enjoy a discount",
          }}
          values={{
            discountPercentageNumber: bpsToPercent(
              collection.collection_promo?.discount_bps ?? 0
            ),
          }}
        />
      </Typography>
    );
  }
  if (showDiscountPercentageTag) {
    return (
      <Typography color={Decorative.shade.orange}>
        <StrictLocalMsg
          defaultMessage="{discountPercentageNumber}% off"
          description={{
            text: "The product in this collection enjoy a discount",
          }}
          values={{
            discountPercentageNumber: bpsToPercent(
              collection.collection_promo?.discount_bps ?? 0
            ),
          }}
        />
      </Typography>
    );
  }
  if (showFreeDisplayTag) {
    return (
      <Typography color={Decorative.shade.orange}>
        <StrictLocalMsg
          defaultMessage="Free display"
          description={{
            text: "This collection offers a free display product",
          }}
        />
      </Typography>
    );
  }
  return null;
};

const useCollectionLinkTo = (collection: IProductBasedCollectionMetadata) => {
  const [linkTo, setLinkTo] = useState<string>(
    getProductBasedCollectionLinkTo(collection.product_based_collection_token)
  );

  useEffect(() => {
    setLinkTo(
      getProductBasedCollectionLinkTo(collection.product_based_collection_token)
    );
  }, [collection.product_based_collection_token]);

  return linkTo;
};

const useCollectionItemsCount = (
  collection: IProductBasedCollectionMetadata
) => {
  const [count, setCount] = useState(localNum(collection.total_items ?? 0));

  useEffect(() => {
    setCount(localNum(collection.total_items ?? 0));
  }, [collection.total_items]);

  return count;
};
